import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import LoadingSpinner from './LoadingSpinner';

type Detail = {
  paymentId: string | number;
  status: 'SUCCESS' | 'FAILED' | 'PENDING';
  invoiceUrl?: string | null;
  message?: string;
};

export default function CheckoutStatusModal() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Detail | null>(null);

  useEffect(() => {
    const onResult = (e: Event) => {
      const d = (e as CustomEvent).detail as Detail;
      setDetail(d);
      setOpen(true);
    };
    window.addEventListener('checkout-result', onResult as any);
    return () => window.removeEventListener('checkout-result', onResult as any);
  }, []);

  useEffect(() => {
    if (!open || !detail || detail.status === 'PENDING') return;
    const timeoutMs = detail.status === 'SUCCESS' ? 2200 : 4500;
    const timer = window.setTimeout(() => setOpen(false), timeoutMs);
    return () => window.clearTimeout(timer);
  }, [detail, open]);

  const color = detail?.status === 'SUCCESS' ? 'text-[hsl(142,71%,45%)]' : detail?.status === 'FAILED' ? 'text-[hsl(0,72%,51%)]' : 'text-[hsl(193,100%,50%)]';
  const title = detail?.status === 'SUCCESS' ? 'Payment completed successfully' : detail?.status === 'FAILED' ? 'Payment failed' : 'Confirming payment';
  const message = detail?.message || (detail?.status === 'SUCCESS'
    ? 'Your purchase was confirmed and your balance is being refreshed.'
    : detail?.status === 'FAILED'
    ? 'The transaction was not confirmed. Please try again or use another payment method.'
    : 'We are waiting for the final provider confirmation.');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="z-[90] max-w-md rounded-3xl border border-[#1f2a3d] bg-[#0b1220] text-white shadow-[0_20px_60px_rgba(0,0,0,0.45)]" overlayClassName="z-[89] bg-black/82">
        <div className="space-y-4 pr-6">
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 ${color}`}>
              {detail?.status === 'PENDING' ? <LoadingSpinner className="h-5 w-5" /> : <span className="text-lg font-semibold">{detail?.status === 'SUCCESS' ? 'OK' : '!'}</span>}
            </div>
            <div className="space-y-1">
              <h2 className={`text-lg font-semibold ${color}`}>{title}</h2>
              <p className="text-sm leading-6 text-gray-300">{message}</p>
            </div>
          </div>

          {detail?.status === 'SUCCESS' && detail?.invoiceUrl && (
            <a className="text-sm underline text-[hsl(193,100%,50%)]" href={detail.invoiceUrl} target="_blank" rel="noreferrer">
              View invoice
            </a>
          )}

          {detail?.status === 'FAILED' && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15 bg-white/5 text-white hover:bg-white/10">
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
